// src/App.tsx

import React, { FC, useLayoutEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ProtectedRoute } from './components/ProtectedRoute';
import { UserRole } from './types';

// Layout & Pages
import Sidebar from './components/Sidebar';
import { TopBar } from './components/TopBar';
import LoginView from './pages/LoginView';
import { DashboardView } from './pages/DashboardView';
import PraticheView from "./pages/PraticheView";
import DettaglioPraticaView from "./pages/DettaglioPraticaView";
import {NuovaPraticaView} from "./pages/NuovaPraticaView";
import { CalendarioView } from "./pages/CalendarioView";
import ReportEditorView from "./pages/ReportEditorView";
import RelazioneDocxView from "./pages/RelazioneDocxView";
import ReportEditorViewBonifiche from "./pages/ReportEditorViewBonifiche";
import ReportEditorViewConsulenza from "./pages/ReportEditorViewConsulenza";
import CollaboratoriView from "./pages/CollaboratoriView";
import { AuthProvider } from "./contexts/AuthContext";
import ReloginModal from "./components/ReloginModal";
import { SidebarProvider, useSidebar } from "./contexts/SidebarContext";
import { ClientiView } from "./pages/ClientView";
import ProfiloView from "./pages/ProfiloView";
import AmministrazioneView from "./pages/AmministrazioneView";
import RegistroMovimentiView from "./pages/RegistroMovimentiView";
import NuovoClienteView from "./pages/NuovoClienteView";
import DettaglioClienteView from "./pages/DettaglioClienteView";
import { ToastProvider } from "./components/ToastContext";
import SediView from "./pages/SediView";
import NuovaSedeView from "./pages/NuovaSedeView";
import AccettazioneInvitoView from "./pages/AccettazioneInvitoView";
import ImpostazioniView from "./pages/ImpostazioniView";
import ConsentGate from "./components/ConsentGate";
import MaintenanceBanner from "./components/MaintenanceBanner";
import ResponsabiliView from "./pages/ResponsabiliView";
import NuovoResponsabileView from "./pages/NuovoResponsabileView";
import RecoverPasswordView from "./pages/RecoverPasswordView";
import ResetPasswordView from "./pages/ResetPasswordView";
import NuovoPreventivoView from "./pages/NuovoPreventivoView";
import PreventiviView from "./pages/PreventiviView";
import UtentiView from "./pages/UtentiView";
import { MieiIncarichiView } from "./pages/MieiIncarichiView";
import ReportMensileView from "./pages/ReportMensileView";
import GestioneServizioView from "./pages/GestioneServizioView";
import ServiziView from "./pages/ServiziView";
import FileManagerView from "./pages/FileManagerView";
import {SpeseCollaboratoreView} from "./pages/SpeseCollaboratoreView";
import StoricoPraticaView from "./pages/StoricoPraticaView";
import AuditLogsView from "./pages/AuditLogsView";
import {ApprovazioneSpeseView} from "./pages/ApprovazioneSpeseView";
import FirmaDigitaleView from "./pages/FirmaDigitaleView";
import TestOtpView from "./pages/TestOtpView";
import PendingApprovalsView from "./pages/PendingApprovalsView";
import ApprovazioniView from "./pages/ApprovazioniView";
import PrivacyPolicyView from "./pages/PrivacyPolicyView";
import TerminiCondizioniView from "./pages/TerminiCondizioniView";
import CookiePolicyView from "./pages/CookiePolicyView";
import LegalFooter from "./components/LegalFooter";

const SidebarOverlay: FC = () => {
    const { isOpen, close } = useSidebar();
    if (!isOpen) return null;
    return (
        <div
            onClick={close}
            className="fixed inset-0 bg-black/40 z-30 lg:hidden"
            aria-hidden="true"
        />
    );
};

const MainLayout: FC<{ children: React.ReactNode }> = ({ children }) => {
    // Il layout resta montato tra una route e l'altra, quindi il <main> che scorre
    // manterrebbe la posizione: a ogni cambio pagina si torna in cima.
    const mainRef = useRef<HTMLElement>(null);
    const { pathname } = useLocation();
    useLayoutEffect(() => {
        mainRef.current?.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
    }, [pathname]);
    return (
        <div className="flex h-screen bg-gray-50 font-sans text-gray-900 overflow-hidden">
            <Sidebar />
            <SidebarOverlay />
            <div className="flex-1 flex flex-col min-w-0">
                <MaintenanceBanner />
                <TopBar />
                <main ref={mainRef} className="flex-1 overflow-y-auto scroll-smooth">
                    {children}
                    <LegalFooter className="mt-6 border-t border-slate-200/70" />
                </main>
            </div>
            {/* Blocca l'app finché l'utente non ri-accetta i consensi aggiornati */}
            <ConsentGate />
        </div>
    );
};

const EditorLayout: FC<{ children: React.ReactNode }> = ({ children }) => {
    return (
        <div className="flex h-screen bg-gray-50 font-sans text-gray-900 overflow-hidden">
            <Sidebar />
            <SidebarOverlay />
            <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
                <MaintenanceBanner />
                {children}
            </div>
        </div>
    );
};

const AppRoutes: FC = () => {
    return (
        <Routes>
            <Route path="/login" element={<LoginView />} />
            <Route path="/recover-password" element={<RecoverPasswordView />} />
            <Route path="/reset-password" element={<ResetPasswordView />} />
            <Route path="/invite/:token" element={<AccettazioneInvitoView />} />
            {/* Pagine legali pubbliche (senza login), linkate anche dagli store. */}
            <Route path="/privacy-policy" element={<PrivacyPolicyView />} />
            <Route path="/termini-condizioni" element={<TerminiCondizioniView />} />
            <Route path="/cookie-policy" element={<CookiePolicyView />} />

            {/* Rotte per tutti gli utenti autenticati */}
            <Route element={<ProtectedRoute />}>
                <Route path="/dashboard" element={<MainLayout><DashboardView /></MainLayout>} />
                <Route path="/le-mie-spese" element={<MainLayout><SpeseCollaboratoreView /></MainLayout>} />

                {/* Incarichi Operativi */}
                <Route path="/incarichi" element={<MainLayout><MieiIncarichiView /></MainLayout>} />

                {/* Pratiche */}
                <Route path="/pratiche" element={<MainLayout><PraticheView /></MainLayout>} />
                <Route path="/pratiche/nuova" element={<MainLayout><NuovaPraticaView /></MainLayout>} />
                <Route path="/pratiche/modifica/:id" element={<MainLayout><NuovaPraticaView /></MainLayout>} />
                <Route path="/pratiche/:id" element={<MainLayout><DettaglioPraticaView /></MainLayout>} />


                <Route path="/profilo" element={<MainLayout><ProfiloView /></MainLayout>} />
                <Route path="/calendario" element={<MainLayout><CalendarioView /></MainLayout>} />
                <Route path="/report-editor/:id" element={<EditorLayout><ReportEditorView /></EditorLayout>} />
                <Route path="/report-editor-bonifiche/:id" element={<EditorLayout><ReportEditorViewBonifiche /></EditorLayout>} />
                <Route path="/report-editor-consulenza/:id" element={<EditorLayout><ReportEditorViewConsulenza /></EditorLayout>} />
                <Route path="/relazione-docx/:id" element={<EditorLayout><RelazioneDocxView /></EditorLayout>} />
            </Route>

            {/* Rotte per admin e responsabili */}
            <Route element={<ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.RESPONSABILE]} />}>
                <Route path="/pratiche/:id/storico/:versionId" element={
                    <StoricoPraticaView />
                } />
                {/* Preventivi */}
                <Route path="/preventivi" element={<MainLayout><PreventiviView /></MainLayout>} />
                <Route path="/preventivi/nuovo" element={<MainLayout><NuovoPreventivoView /></MainLayout>} />
                <Route path="/preventivi/:id" element={<MainLayout><NuovoPreventivoView /></MainLayout>} />
                <Route path="/collaboratori" element={<MainLayout><CollaboratoriView /></MainLayout>} />
                <Route path="/utenti" element={<MainLayout><UtentiView /></MainLayout>} />
                <Route path="/amministrazione/approvazione-spese" element={<MainLayout><ApprovazioneSpeseView /></MainLayout>} />
                <Route path="/pending-approvals" element={<MainLayout><PendingApprovalsView /></MainLayout>} />
                {/* Hub approvazioni unificato; le due rotte sopra restano come
                    deep-link per i widget esistenti. */}
                <Route path="/approvazioni" element={<MainLayout><ApprovazioniView /></MainLayout>} />
                <Route path="/clienti" element={<MainLayout><ClientiView /></MainLayout>} />
                <Route path="/clienti/nuovo" element={<MainLayout><NuovoClienteView /></MainLayout>} />
                <Route path="/clienti/:id" element={<MainLayout><DettaglioClienteView /></MainLayout>} />

                <Route path="/impostazioni" element={<MainLayout><ImpostazioniView /></MainLayout>} />
                <Route path="/firma-digitale" element={<EditorLayout><FirmaDigitaleView /></EditorLayout>} />
                {/* Pagina di prova OtpService via API (non linkata: solo da URL). */}
                <Route path="/testotp" element={<MainLayout><TestOtpView /></MainLayout>} />
                {/* Servizi fiduciari: visibili anche al responsabile; le azioni
                    sensibili restano protette lato BE. */}
                <Route path="/servizi/:id" element={<MainLayout><GestioneServizioView /></MainLayout>} />
                <Route path="/servizi" element={<MainLayout><ServiziView /></MainLayout>} />
                <Route
                    path="/amministrazione/report-mensile"
                    element={
                            <ReportMensileView />
                    }
                />
                {/* Contabilità e registro movimenti aperti anche al responsabile:
                    la vista filtra per `currentSedeId`, già limitata alle sue sedi. */}
                <Route path="/amministrazione" element={<MainLayout><AmministrazioneView /></MainLayout>} />
                <Route path="/amministrazione/registro-movimenti" element={<MainLayout><RegistroMovimentiView /></MainLayout>} />
                {/* File manager aperto anche al responsabile: il Drive personale resta
                    solo admin e il BE filtra per sede. */}
                <Route path="/file-manager" element={<MainLayout><FileManagerView /></MainLayout>} />
            </Route>

            {/* Rotte solo admin */}
            <Route element={<ProtectedRoute allowedRoles={[UserRole.ADMIN]} />}>
                <Route path="/amministrazione/audit" element={<MainLayout><AuditLogsView /></MainLayout>} />
                <Route path="/responsabili" element={<MainLayout><ResponsabiliView /></MainLayout>} />
                <Route path="/responsabili/nuovo" element={<MainLayout><NuovoResponsabileView /></MainLayout>} />

                {/* Sedi: le modifiche sono solo del titolare anche lato BE. */}
                <Route path="/sedi" element={<MainLayout><SediView /></MainLayout>} />
                <Route path="/sedi/nuova" element={<MainLayout><NuovaSedeView /></MainLayout>} />
                <Route path="/sedi/modifica/:id" element={<MainLayout><NuovaSedeView /></MainLayout>} />

            </Route>

            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            {/* Indirizzo inesistente o non permesso al ruolo (/unauthorized): si torna alla
                dashboard invece della pagina bianca; senza login ci pensa ProtectedRoute. */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
    );
};

const App: FC = () => {
    return (
        <AuthProvider>
            <ToastProvider>
                <SidebarProvider>
                    <BrowserRouter>
                        <AppRoutes />
                    </BrowserRouter>
                    {/* Re-login su 401 senza redirect, per non perdere il lavoro non salvato. */}
                    <ReloginModal />
                </SidebarProvider>
            </ToastProvider>
        </AuthProvider>
    );
};

export default App;